import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { BaseSidebarComponent, SIDEBAR_DATA, SidebarRef } from '@cumlaude/shared-components-overlays';
import { Selection } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { UrlService } from '../../services/url.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CompoundFilterExpression, DataService, FilterExpression, SchoolaanwezigheidDetails } from '../../services/data.service';
import { finalize, map, switchMap } from 'rxjs/operators';
import { AlternatingMode, ColumnDef, createColumnDef, createModel } from '../../shared/components/table/table/table.model';
import { LeerlingCellComponent } from '../../shared/components/table/cells/leerling-cell/leerling-cell.component';
import { TableComponent } from '../../shared/components/table/table/table.component';
import { DataCellComponent } from '../../shared/components/table/cells/data-cell/data-cell.component';
import { QueryParamStateService } from '../../services/query-param-state.service';
import { delay, of } from 'rxjs';
import { CssSpinnerComponent } from '../../layout/css-spinner/css-spinner.component';
import { AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';

export type SchoolaanwezigheidLeerlingSidebarData = {
	reden: string;
	maandJaar: string;
	maand: number;
	schooljaar: string;
	selection: Selection;
	filter: FilterExpression;
};

@Component({
	selector: 'app-schoolaanwezigheid-leerling-sidebar',
	templateUrl: './schoolaanwezigheid-leerling-sidebar.component.html',
	styleUrl: './schoolaanwezigheid-leerling-sidebar.component.scss',
	imports: [BaseSidebarComponent, TableComponent, CssSpinnerComponent, AsyncPipe],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchoolaanwezigheidLeerlingSidebarComponent {
	protected readonly sidebarRef = inject<SidebarRef<void>>(SidebarRef);
	protected readonly data = inject<SchoolaanwezigheidLeerlingSidebarData>(SIDEBAR_DATA);
	private readonly urlService = inject(UrlService);
	private readonly dataService = inject(DataService);
	private readonly qp: QueryParamStateService = inject(QueryParamStateService);
	private readonly router = inject(Router);

	protected readonly sidebarTitle = computed(() => `${this.data.reden}, ${this.data.maandJaar}`);
	protected readonly selection = computed(() => this.data.selection);

	loading = signal(false);

	spinnerDelay = of(true).pipe(delay(200));

	openLeerlingDetails(row: SchoolaanwezigheidDetails) {
		const baseSelection = this.selection();
		const urlTree = this.router.parseUrl(baseSelection.detailUrl);
		const queryParams = {
			...urlTree.queryParams,
			selectie: row.aw_nr_leerling!,
			'reden-maand': this.data.maand,
		};
		const selection = <Selection>{
			...baseSelection,
			detailUrl: this.router.serializeUrl(Object.assign(urlTree, { queryParams })),
		};
		this.urlService.navigateToSelection(selection, this.data.schooljaar);
		this.sidebarRef.requestClose();
	}

	leerlingen = toSignal(
		toObservable(this.selection).pipe(
			switchMap((selection) => {
				this.loading.set(true);
				return this.dataService
					.getSchoolAanwezigheidDetails({
						g: ['aw_nr_leerling'],
						f: new CompoundFilterExpression([selection.expression, this.data.filter]),
						month: [],
						sortdir: 'asc',
					})
					.pipe(finalize(() => this.loading.set(false)));
			}),
			map((response) => response.rows)
		),
		{
			initialValue: [],
		}
	);

	sidebarSortOrder = this.qp.signal('sidebarSortOrder', { active: 'Gemiste tijd', direction: 'desc' });

	tableModel = computed(() => {
		let leerlingen = this.leerlingen();
		const sortOrder = this.sidebarSortOrder();

		leerlingen = [...leerlingen].sort((a, b) => {
			const gemistA = a.aw_m_nr_gemist ?? 0;
			const gemistB = b.aw_m_nr_gemist ?? 0;
			return gemistB - gemistA;
		});

		if (sortOrder.direction === 'asc') {
			leerlingen = leerlingen.reverse();
		}

		const model = createModel(leerlingen, (row) => `${row.aw_nr_leerling!}`);
		model.columnDefs = [this.getLeerlingColumnDef(), this.getLesurenColumnDef(), this.getGemisteTijdColumnDef()];
		model.rowsClickable = true;
		model.alternating = AlternatingMode.COLOR;
		model.getRowgroup = (row: SchoolaanwezigheidDetails) => ({ groupId: leerlingen.indexOf(row), lastOfGroup: false });

		return model;
	});

	private getLesurenColumnDef(): ColumnDef<SchoolaanwezigheidDetails> {
		const columnDef = createColumnDef<SchoolaanwezigheidDetails>('Lesuren', 'Lesuren', false, (row) => `${row.aw_m_nr_lesuren!}`);

		columnDef.body.component = DataCellComponent;

		return columnDef;
	}

	private getLeerlingColumnDef(): ColumnDef<SchoolaanwezigheidDetails> {
		const columnDef = createColumnDef<SchoolaanwezigheidDetails>('Leerling');

		columnDef.body.component = LeerlingCellComponent;
		columnDef.body.getValue = (row) => ({
			leerlingNummer: row.aw_nr_leerling!,
			leerlingNaam: row.ll_fun_volledige_naam!,
			pasfoto: row.ll_pic_pasfoto!,
		});

		return columnDef;
	}

	private getGemisteTijdColumnDef(): ColumnDef<SchoolaanwezigheidDetails> {
		const columnDef = createColumnDef<SchoolaanwezigheidDetails>('Gemiste tijd', 'Gemiste tijd', true, (row) => `${row.aw_m_nr_gemist!}`);

		columnDef.body.component = DataCellComponent;
		columnDef.body.dataType = 'minutes';
		columnDef.body.format = 'hu mm';

		return columnDef;
	}
}
